import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField } from '@mui/material';
import Url from '../../data/url';
import {
  GENDER,
  ON_KIDS_VISITED,
  ON_KIDS_DRAFT_NAME,
  ON_KIDS_DRAFT_BIRTH,
  ON_KIDS_DRAFT_GENDER,
} from '../../data/define';
import BottomFixedBtn from '../../component/common/BottomFixedBtn';
import TwoBtns from '../../component/common/TwoBtns';
import BirthDatePickerDialog from '../../component/register/BirthDatePickerDialog';
import { useTranslation } from '../../hook/useTranslation';

function FirstAccessRegisterKids() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [nameInput, setNameInput] = useState({ value: '', isDone: false });
  const [birthInput, setBirthInput] = useState<string | null>(null);
  const [genderInput, setGenderInput] = useState<(typeof GENDER)[keyof typeof GENDER]>(
    GENDER.UNKNOWN
  );
  const [focusField, setFocusField] = useState<string | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const birthInputRef = useRef<HTMLInputElement>(null);

  const handleDraftRegisterKid = useCallback(() => {
    localStorage.setItem(ON_KIDS_VISITED, 'true');
    localStorage.setItem(ON_KIDS_DRAFT_NAME, nameInput.value);
    localStorage.setItem(ON_KIDS_DRAFT_BIRTH, birthInput);
    localStorage.setItem(ON_KIDS_DRAFT_GENDER, genderInput);
    navigate(Url.Home);
  }, [nameInput, birthInput, genderInput, navigate]);

  const handleInstantLogin = () => {
    localStorage.setItem(ON_KIDS_VISITED, 'true');
    navigate(Url.Login);
  };

  // 이름 입력 완료 후 생년월일 필드에 자동 포커스 및 다이얼로그 열기
  useEffect(() => {
    if (nameInput.isDone && !birthInput && birthInputRef.current) {
      setTimeout(() => {
        birthInputRef.current?.focus();
        setDatePickerOpen(true);
      }, 100);
    }
  }, [nameInput.isDone, birthInput]);

  return (
    <>
      <div className="pageInner bg-[#FFF]">
        <div className="w-full flex justify-end mt-2">
          <button
            type="button"
            className="text-[#969696] text-[14px] font-[400] underline"
            onClick={() => {
              localStorage.setItem(ON_KIDS_VISITED, 'true');
              navigate(Url.Home);
            }}
          >
            {t('login.first_access.skip')}
          </button>
        </div>
        {!nameInput.isDone && !birthInput && (
          <div className="my-[30px] w-full text-[24px] font-[800]">
            <div className="w-full text-left">
              {t('login.first_access.name_title').split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  {i === 0 && <br />}
                </span>
              ))}
            </div>
          </div>
        )}
        {nameInput.isDone && !birthInput && (
          <div className="my-[30px] w-full text-[24px] font-[800]">
            <div className="w-full text-left">
              {t('login.first_access.birth_title').split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  {i === 0 && <br />}
                </span>
              ))}
            </div>
          </div>
        )}
        {nameInput.isDone && birthInput && (
          <div className="my-[30px] w-full text-[24px] font-[800]">
            <div className="w-full text-left">
              {t('login.first_access.gender_title').split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  {i === 0 && <br />}
                </span>
              ))}
            </div>
          </div>
        )}
        {birthInput && (
          <div className="w-full flex flex-col items-start justify-start gap-2 mb-[28px]">
            <div className="font-[400] text-[14px] text-[#363636]">{t('login.first_access.gender')}</div>
            <TwoBtns
              leftTitle={t('login.first_access.male')}
              rightTitle={t('login.first_access.female')}
              onLeftClick={() => setGenderInput(GENDER.MALE)}
              onRightClick={() => setGenderInput(GENDER.FEMALE)}
              leftFocus={genderInput === GENDER.MALE}
              rightFocus={genderInput === GENDER.FEMALE}
            />
          </div>
        )}
        {nameInput.isDone && (
          <div className="w-full flex flex-col items-start justify-start gap-2 mb-[28px]">
            <div
              className="font-[400] text-[14px] text-[#363636]"
              style={{ color: focusField === 'birth' ? '#5EBEBB' : '#363636' }}
            >
              {t('login.first_access.birth')}
            </div>
            <TextField
              inputRef={birthInputRef}
              variant="standard"
              value={birthInput ?? ''}
              fullWidth
              placeholder="0000-00-00"
              onClick={() => setDatePickerOpen(true)}
              onFocus={() => {
                setFocusField('birth');
              }}
              onBlur={() => setFocusField(null)}
            />
            <BirthDatePickerDialog
              open={datePickerOpen}
              onClose={() => {
                setDatePickerOpen(false);
                setTimeout(() => {
                  birthInputRef.current?.blur();
                  setFocusField(null);
                }, 150);
              }}
              onConfirm={() => {
                setDatePickerOpen(false);
                setTimeout(() => {
                  birthInputRef.current?.blur();
                  setFocusField(null);
                }, 150);
              }}
              birthInput={birthInput || ''}
              setBirthInput={setBirthInput as any}
            />
          </div>
        )}
        <div className="w-full flex flex-col items-start justify-start gap-2">
          <div
            className="font-[400] text-[14px] text-[#363636]"
            style={{ color: focusField === 'name' ? '#5EBEBB' : '#363636' }}
          >
            {t('login.first_access.name')}
          </div>
          <TextField
            variant="standard"
            value={nameInput.value}
            onChange={(e) => {
              const inputValue = e.target.value;
              setNameInput((prev) => ({ ...prev, value: inputValue }));
            }}
            fullWidth
            placeholder={t('login.first_access.name_placeholder')}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                if (nameInput.value.length < 2) return;
                setNameInput((prev) => ({ ...prev, isDone: true }));
              }
            }}
            onFocus={() => setFocusField('name')}
            onBlur={() => setFocusField(null)}
          />
        </div>
      </div>
      <BottomFixedBtn
        title={t('login.first_access.btn_register')}
        onClick={handleDraftRegisterKid}
        disabled={
          nameInput.value.length < 1 || birthInput?.length < 8 || genderInput === GENDER.UNKNOWN
        }
      />
      <div className="w-full flex justify-center pb-5">
        <button
          type="button"
          className="text-[#767676] text-[16px] font-[400]"
          onClick={handleInstantLogin}
        >
          {t('login.first_access.btn_login')}
        </button>
      </div>
    </>
  );
}

export default FirstAccessRegisterKids;
